import { Injectable } from '@angular/core';
import { map, tap } from 'rxjs/operators';
import { ApiService } from '../../shared/services/core/api.service';
import { of, Observable } from 'rxjs';
import { RewardsWalletStore } from './rewards-wallet.store';
import { RewardsWalletQuery } from './rewards-wallet.query';
import { RewardsWalletItem } from './models/rewards-wallet.model';
import { MarketplaceWalletItem } from './models/marketplace-wallet.model';
import { WalletItemType } from './models/rewards-wallet-type.enum';
import { guid, withTransaction } from '@datorama/akita';
import { MartketplaceTransferredItem } from '../marketplace/models/marketplace-transferred-item.model';

@Injectable({ providedIn: 'root' })
export class RewardsWalletService {
  constructor(
    private rewardsWalletStore: RewardsWalletStore,
    private rewardsWalletQuery: RewardsWalletQuery,
    private api: ApiService
  ) {}

  /** Fetches the wallet items and puts them in the store. */
  fetchWalletItems(): Observable<null> {
    this.rewardsWalletStore.setLoading(true);
    const request$: Observable<null> = this.api.get<RewardsWalletItem>('purchases/wallet?showExpired=true').pipe(
      withTransaction((rewardItems) => {
        rewardItems.forEach((item: any) => {
          item.UniqueId = guid();
          if (item.ItemType == WalletItemType.Marketplace) {
            item.WalletId = (item as MarketplaceWalletItem).LogId + '_' + item.ItemType;
          } else {
            item.WalletId = item.Id + '_' + item.ItemType;
          }
        });
        this.rewardsWalletStore.set(rewardItems);
        this.rewardsWalletStore.setGroupedWalletItems(rewardItems);
      }),
      map(() => null)
    );
    return this.rewardsWalletQuery.getHasCache() ? of(null) : request$;
  }

  /**
   * This will disable cache of the rewards wallet store, so
   * that a fetch can be made again.
   */
  disableRewardsWalletStoreCache() {
    this.rewardsWalletStore.setHasCache(false);
  }

  /** Transfer marketplace items to another member or partner */
  transferItem(
    payload: Partial<{ TransferLogId: number; TransferPortalId: string; TransferOrderId: string; EmailAddress: string }[]>
  ) {
    return this.api.post('marketplace/transfer', payload).pipe(
      tap({
        next: () => {
          payload.forEach((item: any) => {
            this.rewardsWalletStore.remove((entity: any) => entity.LogId === item.TransferLogId);
          });
        },
      })
    );
  }

  /** Fetches the marketplace transferred items and puts them in the store. */
  fetchTransferredItems(): Observable<MartketplaceTransferredItem[]> {
    return this.api
      .get<MartketplaceTransferredItem>('purchases/wallet/transfered')
      .pipe(tap((transferredItems) => this.rewardsWalletStore.update({ transferredItems })));
  }

  /** Call back an item and remove the item from the store so it doesnt need to fetch again, However this call does not return data or model to be added to the wallet page, so we need to fetch wallet again to update.
   */
  callbackItem(item: MartketplaceTransferredItem): Observable<any> {
    return this.api.post(`marketplace/transfer/callback/${item.LogId}`, {}).pipe(
      tap({
        next: () => {
          this.rewardsWalletStore.update((state) => {
            const updatedItems = state.transferredItems?.filter((i) => i.LogId !== item.LogId) || null;
            return { ...state, transferredItems: updatedItems };
          });
        },
      })
    );
  }
}
